import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {pluralize} from 'BenchlingUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import ErrorIcon from 'ui-library/icons/ErrorIcon';

export interface TableValidationBypassTooltipProps {
  errors: Array<string>;
}

/**
 * A tooltip that displays validation errors for a structured table.
 */
export default function TableValidationBypassTooltip({
  errors,
}: TableValidationBypassTooltipProps): JSX.Element {
  const uniqueErrors = useMemo(() => [...new Set(errors)], [errors]);

  return (
    <>
      <div className={css(styles.title)}>Resolve errors or submit and bypass validation</div>
      <div className={css(styles.description)}>
        {pluralize(uniqueErrors.length, 'Validation error', 'Multiple validation errors')} detected.
      </div>
      <>
        {uniqueErrors.map((error, index) => (
          <div className={css(styles.errorRow)} key={index}>
            <div className={css(styles.errorIconColumn)}>
              <ErrorIcon className={css(styles.errorIcon)} />
            </div>
            <div className={css(styles.errorMessageColumn)}>
              <div
                className={css(styles.errorMessageText)}
                {...getTestProps({
                  component: 'TableValidationBypassTooltip',
                  element: 'errorRow',
                  index: `${index}`,
                })}
              >
                {error}
              </div>
            </div>
          </div>
        ))}
      </>
    </>
  );
}

export const styles = StyleSheet.create({
  title: {
    fontSize: '13px',
    fontWeight: 'bold',
    marginBottom: '2px',
  },
  description: {
    fontSize: '12px',
    lineHeight: '14px',
  },
  errorRow: {
    display: 'flex',
    margin: '5px 0 2px 0',
  },
  errorIconColumn: {
    margin: 'auto', // Center the icon
    flex: '0 0 20px', // Fixed width and flexible height
  },
  errorIcon: {
    color: Color.RED_400,
  },
  errorMessageColumn: {
    wordWrap: 'break-word', // Wrap text to next line
    flex: '1', // Flexible width
  },
  errorMessageText: {
    fontSize: '12px',
    lineHeight: '14px',
  },
});
